import React, { useState, useRef } from 'react';
import { Upload, ArrowRight, ArrowLeft, Settings, FileText, CheckCircle, XCircle } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';

const BOOK_FIELDS = [
  { key: 'title',             label: 'სათაური *',           required: true },
  { key: 'title_remainder',   label: 'ქვესათაური'                          },
  { key: 'author',            label: 'ავტორი'                               },
  { key: 'isbn',              label: 'ISBN'                                 },
  { key: 'publisher',         label: 'გამომცემელი'                         },
  { key: 'pub_year',          label: 'გამოცემის წელი'                      },
  { key: 'edition',           label: 'გამოცემა'                            },
  { key: 'pages',             label: 'გვერდები'                            },
  { key: 'language',          label: 'ენა'                                  },
  { key: 'description',       label: 'აღწერა'                              },
  { key: 'subject',           label: 'საგანი/თემა'                         },
  { key: 'series',            label: 'სერია'                               },
  { key: 'volume',            label: 'ტომი'                                },
  { key: 'udc',               label: 'UDC კლასიფიკაცია'                    },
  { key: 'account_code',      label: 'ანგარიშის კოდი'                      },
  { key: 'sub_account',       label: 'ქვეანგარიში'                         },
  { key: 'acquisition_price', label: 'შეძენის ფასი'                        },
  { key: 'total_copies',      label: 'ასლების რაოდენობა'                   },
  { key: 'inv_number',        label: 'საინვენტარო ნომერი'                  },
  { key: 'location',          label: 'მდებარეობა (დარბაზი)'                },
  { key: 'call_number',       label: 'შიფრი (call number)'                 },
  { key: 'notes',             label: 'შენიშვნა'                            },
  { key: 'full_account',     label: 'სრული საბუღ. კოდი'                   },
];

const MEMBER_FIELDS = [
  { key: 'first_name',  label: 'სახელი *',      required: true },
  { key: 'last_name',   label: 'გვარი *',        required: true },
  { key: 'email',       label: 'ელფოსტა'                       },
  { key: 'phone',       label: 'ტელეფონი'                      },
  { key: 'id_number',   label: 'პირადი ნომერი'                 },
  { key: 'barcode',     label: 'ბარკოდი'                       },
  { key: 'member_type', label: 'ტიპი'                          },
  { key: 'faculty',     label: 'ფაკულტეტი'                    },
  { key: 'notes',       label: 'შენიშვნა'                      },
];

type ImportType = 'books' | 'members';
type Step = 'upload' | 'map' | 'options' | 'result';

export default function ImportPage() {
  const [type, setType] = useState<ImportType>('books');
  const [step, setStep] = useState<Step>('upload');
  const [loading, setLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [fileObj, setFileObj] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [preview, setPreview] = useState<any[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [options, setOptions] = useState({ skipDuplicates: true, skipEmpty: true });
  const [result, setResult] = useState<any>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const fields = type === 'books' ? BOOK_FIELDS : MEMBER_FIELDS;

  const card: React.CSSProperties = { background: 'var(--bg-card)', border: '1px solid #1e2d45', borderRadius: 12 };
  const btn = (active?: boolean): React.CSSProperties => ({ padding: '8px 20px', borderRadius: 8, border: `1px solid ${active ? 'var(--border-active)' : 'var(--border)'}`, background: active ? 'var(--bg-active)' : 'var(--bg-dark2)', color: active ? 'var(--accent)' : 'var(--text-muted)', fontSize: 13, cursor: 'pointer' });
  const sel: React.CSSProperties = { width: '100%', background: 'var(--bg-input)', border: '1px solid #1e2d45', borderRadius: 6, padding: '6px 8px', fontSize: 12, color: 'var(--text-secondary)', outline: 'none' };

  const handleFile = async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['xlsx','xls','csv'].includes(ext || '')) return toast.error('მხოლოდ XLSX, XLS, CSV');
    setFileObj(file); setLoading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.post('/bookimport/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHeaders(data.headers); setPreview(data.preview); setTotalRows(data.total);
      const autoMap: Record<string, string> = {};
      fields.forEach(f => {
        const m = data.headers.find((h: string) => h.toLowerCase().includes(f.key.toLowerCase()));
        if (m) autoMap[f.key] = m;
      });
      setMapping(autoMap); setStep('map');
    } catch (err: any) { toast.error(err?.response?.data?.error || 'ფაილი ვერ წაიკითხა'); }
    finally { setLoading(false); }
  };

  const handleImport = async () => {
    if (!fileObj) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', fileObj);
      fd.append('mapping', JSON.stringify(mapping));
      fd.append('options', JSON.stringify(options));
      const endpoint = type === 'books' ? '/bookimport/run' : '/import/members';
      const { data } = await api.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data); setStep('result');
      toast.success(`${data.inserted ?? data.imported ?? 0} ჩანაწერი დაემატა!`);
    } catch (err: any) { toast.error(err?.response?.data?.error || 'Import შეცდომა'); }
    finally { setLoading(false); }
  };

  const reset = () => { setStep('upload'); setFileObj(null); setHeaders([]); setPreview([]); setMapping({}); setResult(null); setTotalRows(0); };

  const steps = ['upload','map','options','result'];
  const stepLabels = ['1. ფაილი','2. Mapping','3. პარამეტრები','4. შედეგი'];

  return (
    <div style={{ padding: 24, fontFamily: 'system-ui,sans-serif', maxWidth: 900 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Import Wizard</h1>
        <p style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>XLSX / CSV ფაილიდან წიგნების და წევრების მასობრივი ატვირთვა</p>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {(['books','members'] as ImportType[]).map(t => (
          <button key={t} onClick={() => { setType(t); reset(); }} style={btn(type === t)}>
            {t === 'books' ? '📚 წიგნები' : '👤 წევრები'}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 24 }}>
        {steps.map((s, i) => (
          <React.Fragment key={s}>
            <div style={{ padding: '5px 12px', borderRadius: 20, background: step === s ? 'var(--bg-active)' : 'transparent', border: `1px solid ${step === s ? 'var(--border-active)' : 'transparent'}` }}>
              <span style={{ fontSize: 12, color: step === s ? 'var(--accent)' : 'var(--text-dimmed)' }}>{stepLabels[i]}</span>
            </div>
            {i < steps.length - 1 && <ArrowRight size={12} style={{ color: 'var(--border)' }} />}
          </React.Fragment>
        ))}
      </div>

      {step === 'upload' && (
        <div>
          <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
            onClick={() => inputRef.current?.click()}
            style={{ border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 12, padding: 60, textAlign: 'center', cursor: 'pointer', background: dragOver ? 'var(--accent-bg)' : 'transparent', transition: 'all 0.2s' }}>
            {loading
              ? <><div style={{ width: 36, height: 36, border: '3px solid #1e2d45', borderTop: '3px solid #60a5fa', borderRadius: '50%', margin: '0 auto 12px', animation: 'spin 1s linear infinite' }} /><p style={{ color: 'var(--text-muted)', fontSize: 13 }}>ფაილი იხსნება...</p></>
              : <><Upload size={40} style={{ color: dragOver ? 'var(--accent)' : 'var(--text-dimmed)', margin: '0 auto 16px', display: 'block' }} /><p style={{ color: 'var(--text-secondary)', fontSize: 14, fontWeight: 500, marginBottom: 6 }}>XLSX / XLS / CSV ჩააგდე ან დააჭირე</p><p style={{ color: 'var(--text-dimmed)', fontSize: 12 }}>მაქსიმუმ 50MB</p></>}
          </div>
          <div style={{ ...card, padding: 14, marginTop: 16 }}>
            <p style={{ fontSize: 12, color: 'var(--text-faint)', margin: 0 }}>
              <span style={{ color: 'var(--accent)', fontWeight: 500 }}>მინიშნება:</span> ფაილს შეიძლება ჰქონდეს ნებისმიერი სვეტების სახელები — შემდეგ ნაბიჯში შეაფარდებ Folios-ის ველებთან. სავალდებულო: <span style={{ color: 'var(--warning)' }}>სათაური</span>.
            </p>
          </div>
        </div>
      )}

      {step === 'map' && (
        <div>
          <div style={{ ...card, padding: 14, marginBottom: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <FileText size={14} style={{ color: 'var(--accent)' }} />
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{fileObj?.name}</span>
              <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>— {totalRows} სტრიქონი</span>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #1e2d45' }}>
                    {headers.map(h => <th key={h} style={{ padding: '5px 8px', textAlign: 'left', color: 'var(--accent)', fontWeight: 500, whiteSpace: 'nowrap' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((row, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #0f1826' }}>
                      {headers.map(h => <td key={h} style={{ padding: '4px 8px', color: 'var(--text-muted)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{String(row[h] ?? '')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={card}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid #1e2d45' }}>
              <Settings size={14} style={{ display: 'inline', marginRight: 6, color: 'var(--accent)' }} />
              <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)' }}>ველების შეფარდება</span>
              <span style={{ fontSize: 11, color: 'var(--text-faint)', marginLeft: 8 }}>ფაილის სვეტი → Folios ველი</span>
            </div>
            <div style={{ padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {fields.map(f => (
                <div key={f.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'var(--bg-input)', borderRadius: 7, border: `1px solid ${mapping[f.key] && mapping[f.key] !== '__skip__' ? 'var(--border-active)' : 'var(--border)'}` }}>
                  <span style={{ fontSize: 11, color: mapping[f.key] && mapping[f.key] !== '__skip__' ? 'var(--accent)' : 'var(--text-faint)', minWidth: 150, flexShrink: 0 }}>{f.label}</span>
                  <select value={mapping[f.key] || '__skip__'} onChange={e => setMapping(p => ({ ...p, [f.key]: e.target.value }))} style={sel}>
                    <option value="__skip__">— გამოტოვება —</option>
                    {headers.map(h => <option key={h} value={h}>{h}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14 }}>
            <button onClick={reset} style={{ ...btn(), display: 'flex', alignItems: 'center', gap: 6 }}><ArrowLeft size={14} /> უკან</button>
            <button onClick={() => setStep('options')} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 24px', background: '#1d4ed8', border: 'none', borderRadius: 8, color: 'white', fontSize: 13, cursor: 'pointer' }}>შემდეგი <ArrowRight size={14} /></button>
          </div>
        </div>
      )}

      {step === 'options' && (
        <div>
          <div style={{ ...card, padding: 20, marginBottom: 14 }}>
            <h3 style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-secondary)', margin: '0 0 14px' }}>Import პარამეტრები</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={options.skipDuplicates} onChange={e => setOptions(p => ({ ...p, skipDuplicates: e.target.checked }))} style={{ width: 16, height: 16, marginTop: 2, accentColor: '#3b82f6' }} />
                <div><div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>დუბლიკატების გამოტოვება</div><div style={{ fontSize: 11, color: 'var(--text-faint)' }}>ანგარიშის კოდი და სათაური ემთხვევა — გამოტოვდეს</div></div>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={options.skipEmpty} onChange={e => setOptions(p => ({ ...p, skipEmpty: e.target.checked }))} style={{ width: 16, height: 16, marginTop: 2, accentColor: '#3b82f6' }} />
                <div><div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>ცარიელი სათაური — გამოტოვება</div><div style={{ fontSize: 11, color: 'var(--text-faint)' }}>სავალდებულო ველის გარეშე სტრიქონი გამოტოვდეს</div></div>
              </label>
            </div>
          </div>

          <div style={{ ...card, padding: 14, marginBottom: 14 }}>
            <h3 style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', margin: '0 0 10px' }}>შეჯამება</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 8 }}>
              {[
                { label: 'ფაილი', val: fileObj?.name?.substring(0,20)+'...', color: 'var(--text-secondary)' },
                { label: 'სტრიქონები', val: totalRows, color: 'var(--accent)' },
                { label: 'მიბმული ველი', val: Object.values(mapping).filter(v => v && v !== '__skip__').length, color: 'var(--success)' },
                { label: 'ტიპი', val: type === 'books' ? 'წიგნები' : 'წევრები', color: 'var(--warning)' },
              ].map((item, i) => (
                <div key={i} style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-faint)' }}>{item.label}</div>
                  <div style={{ fontSize: i === 0 ? 11 : 20, fontWeight: i === 0 ? 400 : 600, color: item.color, marginTop: 2 }}>{item.val}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button onClick={() => setStep('map')} style={{ ...btn(), display: 'flex', alignItems: 'center', gap: 6 }}><ArrowLeft size={14} /> უკან</button>
            <button onClick={handleImport} disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 28px', background: loading ? 'var(--border-active)' : '#1d4ed8', border: 'none', borderRadius: 8, color: 'white', fontSize: 14, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer' }}>
              {loading
                ? <><div style={{ width: 16, height: 16, border: '2px solid #ffffff44', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /> იტვირთება...</>
                : <><Upload size={16} /> Import დაწყება</>}
            </button>
          </div>
        </div>
      )}

      {step === 'result' && result && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[
              { icon: <CheckCircle size={28} style={{ color: 'var(--success)', margin: '0 auto 8px', display: 'block' }} />, val: result.inserted ?? result.imported ?? 0, label: 'დაემატა', color: 'var(--success)' },
              { icon: <XCircle size={28} style={{ color: 'var(--warning)', margin: '0 auto 8px', display: 'block' }} />, val: result.skipped ?? 0, label: 'გამოტოვდა', color: 'var(--warning)' },
              { icon: <XCircle size={28} style={{ color: 'var(--danger)', margin: '0 auto 8px', display: 'block' }} />, val: result.errors?.length ?? 0, label: 'შეცდომა', color: 'var(--danger)' },
            ].map((item, i) => (
              <div key={i} style={{ ...card, padding: 20, textAlign: 'center' }}>
                {item.icon}
                <div style={{ fontSize: 32, fontWeight: 700, color: item.color }}>{item.val}</div>
                <div style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 4 }}>{item.label}</div>
              </div>
            ))}
          </div>

          {result.errors?.length > 0 && (
            <div style={{ ...card, marginBottom: 16, overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', borderBottom: '1px solid #1e2d45' }}>
                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--danger)' }}>შეცდომების სია</span>
              </div>
              <div style={{ maxHeight: 250, overflowY: 'auto', padding: 12 }}>
                {result.errors.map((e: any, i: number) => (
                  <div key={i} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: '1px solid #0f1826' }}>
                    <XCircle size={13} style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{typeof e === 'string' ? e : `${e.row}: ${e.error}`}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <button onClick={reset} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 24px', background: '#1d4ed8', border: 'none', borderRadius: 8, color: 'white', fontSize: 13, cursor: 'pointer' }}>
            <Upload size={14} /> ახალი Import
          </button>
        </div>
      )}

      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
